import { APP_BUILD_ID, APP_VERSION } from "./generatedVersion";

const isDev = process.env.NODE_ENV !== "production";
const UNKNOWN_NEW_VERSION = "Nueva versión";
const VERSION_URL = "/version.json";
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;
const UPDATE_CHECK_THROTTLE_MS = 30 * 1000;
const UPDATE_PREPARE_TIMEOUT_MS = 8 * 1000;
const UPDATE_ACTIVATION_TIMEOUT_MS = 6 * 1000;
const UPDATE_SNOOZE_MS = 30 * 60 * 1000;
const RELOAD_GUARD_KEY = "vq_pwa_reload_started_at";
const RELOAD_GUARD_MS = 15 * 1000;

export { APP_VERSION };

const log = (...args) => {
  if (isDev) console.log("[PWA]", ...args);
};

let registrationPromise = null;
let notifiedWorker = null;
let notifiedVersion = null;
let latestPublishedVersion = null;
let nextNotificationAt = 0;
let refreshing = false;
let activationRequested = false;
let controllerListenerReady = false;
let proactiveChecksReady = false;
let updateCheckPromise = null;
let lastUpdateCheckAt = 0;
let activationFallbackTimer = null;
const updateListeners = new Set();

const browserFetch = (...args) => window.fetch(...args);

const storageNumber = key => {
  try { return Number(window.sessionStorage.getItem(key)) || 0; } catch { return 0; }
};

const markReloadCompleted = () => {
  const startedAt = storageNumber(RELOAD_GUARD_KEY);
  if (!startedAt) return;
  try { window.sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* storage unavailable */ }
  log("recarga completada", { version: APP_VERSION, elapsedMs: Math.max(0, Date.now() - startedAt) });
};

const reloadOnce = reason => {
  if (refreshing) return false;
  const now = Date.now();
  const previous = storageNumber(RELOAD_GUARD_KEY);
  if (previous && now - previous < RELOAD_GUARD_MS) {
    log("recarga duplicada evitada", { reason });
    return false;
  }
  refreshing = true;
  try { window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now)); } catch { /* storage unavailable */ }
  log("recargando una vez", { reason, version: APP_VERSION });
  window.location.reload();
  return true;
};

const setupControllerChange = () => {
  if (controllerListenerReady || !("serviceWorker" in navigator)) return;
  controllerListenerReady = true;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    log("controllerchange ocurrió", { activationRequested });
    // clients.claim() also fires on a first installation. Only an update that the
    // user explicitly accepted is allowed to reload the application.
    if (!activationRequested) return;
    if (activationFallbackTimer) clearTimeout(activationFallbackTimer);
    reloadOnce("controllerchange");
  });
};

const emitUpdate = (registration, waiting, newVersion, detectedBy) => {
  if (Date.now() < nextNotificationAt) return null;
  notifiedVersion = newVersion || UNKNOWN_NEW_VERSION;
  const update = {
    registration,
    waiting: waiting || null,
    currentVersion: APP_VERSION,
    newVersion: notifiedVersion,
    detectedBy,
    preparing: !waiting,
  };
  updateListeners.forEach(listener => listener(update));
  return update;
};

const readWaitingWorkerVersion = waiting => new Promise(resolve => {
  if (!waiting || typeof waiting.postMessage !== "function" || typeof MessageChannel === "undefined") {
    resolve(latestPublishedVersion || UNKNOWN_NEW_VERSION);
    return;
  }

  const channel = new MessageChannel();
  let settled = false;
  const finish = value => {
    if (settled) return;
    settled = true;
    clearTimeout(timeout);
    try { channel.port1.close?.(); } catch { /* noop */ }
    resolve(value || latestPublishedVersion || UNKNOWN_NEW_VERSION);
  };
  const timeout = setTimeout(() => finish(latestPublishedVersion || UNKNOWN_NEW_VERSION), 900);
  channel.port1.onmessage = event => finish(event?.data?.version);

  try {
    waiting.postMessage({ type: "GET_VERSION" }, [channel.port2]);
  } catch {
    finish(latestPublishedVersion || UNKNOWN_NEW_VERSION);
  }
});

const notifyWaitingWorker = async (registration, { force = false } = {}) => {
  const waiting = registration?.waiting;
  if (!waiting) return null;
  if (!force && notifiedWorker === waiting) return null;
  notifiedWorker = waiting;
  log("worker esperando", { currentVersion: APP_VERSION });
  const newVersion = await readWaitingWorkerVersion(waiting);
  if (registration?.waiting !== waiting) return null;
  log("versión nueva identificada", { currentVersion: APP_VERSION, newVersion });
  return emitUpdate(registration, waiting, newVersion, "service-worker");
};

const normalizePublishedVersion = value => {
  if (!value || typeof value !== "object") return null;
  const buildId = String(value.buildId || "").trim();
  const display = String(value.display || "").trim();
  if (!buildId || !display || buildId.length > 80 || display.length > 120) return null;
  return { buildId, display };
};

export const readPublishedVersion = async (fetcher = browserFetch) => {
  if (typeof fetcher !== "function") return null;
  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  const timeout = controller ? setTimeout(() => controller.abort(), 4000) : null;
  try {
    const response = await fetcher(VERSION_URL, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (!response?.ok) return null;
    return normalizePublishedVersion(await response.json());
  } catch (error) {
    log("no se pudo consultar la versión publicada", error?.message || error);
    return null;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
};

const isCurrentVersion = published => published
  && published.buildId === APP_BUILD_ID
  && published.display === APP_VERSION;

export const checkForPwaUpdate = async (registration, fetcher = browserFetch, { force = false } = {}) => {
  if (!registration) return null;
  const now = Date.now();
  if (!force && now - lastUpdateCheckAt < UPDATE_CHECK_THROTTLE_MS) {
    return notifyWaitingWorker(registration, { force: true });
  }
  if (updateCheckPromise) return updateCheckPromise;
  lastUpdateCheckAt = now;
  updateCheckPromise = (async () => {
    const waitingUpdate = await notifyWaitingWorker(registration, { force: true });
    if (waitingUpdate) return waitingUpdate;

    const published = await readPublishedVersion(fetcher);
    if (!published || isCurrentVersion(published)) return null;
    latestPublishedVersion = published.display;
    log("versión publicada diferente", { currentVersion: APP_VERSION, newVersion: published.display });
    try { await registration.update?.(); } catch (error) { log("falló registration.update", error?.message || error); }
    const installedUpdate = await notifyWaitingWorker(registration, { force: true });
    return installedUpdate || emitUpdate(registration, null, published.display, "version-endpoint");
  })().finally(() => { updateCheckPromise = null; });
  return updateCheckPromise;
};

const setupRegistrationLifecycle = registration => {
  registration.addEventListener?.("updatefound", () => {
    const newWorker = registration.installing;
    if (!newWorker) return;
    log("nueva versión detectada", { state: newWorker.state, currentVersion: APP_VERSION });
    newWorker.addEventListener?.("statechange", () => {
      if (newWorker.state !== "installed") return;
      if (navigator.serviceWorker.controller) {
        notifyWaitingWorker(registration, { force: true });
      } else {
        log("primera instalación completada", { version: APP_VERSION });
      }
    });
  });
};

const setupProactiveChecks = (registration, fetcher) => {
  if (proactiveChecksReady) return;
  proactiveChecksReady = true;
  const check = force => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    checkForPwaUpdate(registration, fetcher, { force }).catch(() => {});
  };
  const onVisibility = () => { if (document.visibilityState === "visible") check(false); };
  window.addEventListener("focus", () => check(false));
  window.addEventListener("online", () => check(true));
  document.addEventListener("visibilitychange", onVisibility);
  setInterval(() => check(false), UPDATE_CHECK_INTERVAL_MS);
};

export const registerPwaUpdateHandler = ({
  onUpdateReady,
  fetcher = browserFetch,
  proactive = true,
  checkPublishedVersion = true,
} = {}) => {
  if (!("serviceWorker" in navigator)) {
    log("service worker no soportado");
    return Promise.resolve(null);
  }

  markReloadCompleted();
  setupControllerChange();
  if (onUpdateReady) updateListeners.add(onUpdateReady);

  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker.register("/service-worker.js", { scope: "/", updateViaCache: "none" })
      .then(registration => {
        log("service worker registrado", { scope: registration.scope, version: APP_VERSION });
        setupRegistrationLifecycle(registration);
        notifyWaitingWorker(registration, { force: true });
        if (proactive) setupProactiveChecks(registration, fetcher);
        if (checkPublishedVersion) checkForPwaUpdate(registration, fetcher, { force: true }).catch(() => {});
        else if (typeof registration.update === "function") registration.update().catch(() => {});
        return registration;
      })
      .catch(error => {
        log("error registrando service worker", error?.message || error);
        return null;
      });
  } else {
    registrationPromise.then(registration => {
      if (!registration) return;
      if (registration.waiting) {
        readWaitingWorkerVersion(registration.waiting).then(newVersion => onUpdateReady?.({
          registration,
          waiting: registration.waiting,
          currentVersion: APP_VERSION,
          newVersion,
          detectedBy: "service-worker",
          preparing: false,
        }));
      } else if (latestPublishedVersion && latestPublishedVersion !== APP_VERSION) {
        onUpdateReady?.({
          registration,
          waiting: null,
          currentVersion: APP_VERSION,
          newVersion: latestPublishedVersion,
          detectedBy: "version-endpoint",
          preparing: true,
        });
      }
    });
  }

  return registrationPromise;
};

export const removePwaUpdateListener = listener => {
  if (listener) updateListeners.delete(listener);
};

export const snoozePwaUpdate = (milliseconds = UPDATE_SNOOZE_MS) => {
  nextNotificationAt = Date.now() + Math.max(0, Number(milliseconds) || 0);
};

const waitForWaitingWorker = (registration, timeoutMs = UPDATE_PREPARE_TIMEOUT_MS) => new Promise(resolve => {
  if (registration?.waiting) { resolve(registration.waiting); return; }
  const startedAt = Date.now();
  const poll = () => {
    if (registration?.waiting) { resolve(registration.waiting); return; }
    if (Date.now() - startedAt >= timeoutMs) { resolve(null); return; }
    setTimeout(poll, 100);
  };
  poll();
});

export const applyPwaUpdate = async (registration, {
  prepareTimeoutMs = UPDATE_PREPARE_TIMEOUT_MS,
  activationTimeoutMs = UPDATE_ACTIVATION_TIMEOUT_MS,
} = {}) => {
  if (!registration) return false;
  let waiting = registration.waiting;
  if (!waiting) {
    try { await registration.update?.(); } catch (error) { log("no se pudo preparar la actualización", error?.message || error); }
    waiting = await waitForWaitingWorker(registration, prepareTimeoutMs);
  }
  if (!waiting || typeof waiting.postMessage !== "function") return false;
  log("usuario pulsó Actualizar ahora", { currentVersion: APP_VERSION, newVersion: notifiedVersion });
  try {
    activationRequested = true;
    waiting.postMessage({ type: "SKIP_WAITING" });
  } catch (error) {
    activationRequested = false;
    log("no se pudo activar la actualización", error?.message || error);
    return false;
  }
  if (activationFallbackTimer) clearTimeout(activationFallbackTimer);
  activationFallbackTimer = setTimeout(() => {
    if (activationRequested) reloadOnce("activation-timeout");
  }, activationTimeoutMs);
  return true;
};
